import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Loader2, ShieldCheck, ArrowRight, Building2, Layers } from "lucide-react";
import { Layout } from "@/components/Layout";
import { BusinessCard } from "@/components/BusinessCard";
import { api } from "@/lib/api";

export default function CategoryPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setNotFound(false);
    api
      .get(`/categories/${slug}`)
      .then(({ data }) => {
        if (active) setData(data);
      })
      .catch(() => {
        if (active) setNotFound(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [slug]);

  if (loading)
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="animate-spin text-brand" size={32} />
        </div>
      </Layout>
    );

  if (notFound || !data)
    return (
      <Layout>
        <div className="min-h-[50vh] flex flex-col items-center justify-center text-center">
          <p className="font-display font-bold text-xl text-slate-800">Category not found</p>
          <Link to="/categories" className="mt-4 text-brand font-semibold">
            ← Browse all categories
          </Link>
        </div>
      </Layout>
    );

  const title = `${data.name} Businesses in India | IndiaBusinessCircle`;
  const metaDesc = data.description;
  const canonical = `${window.location.origin}/categories/${data.slug}`;

  return (
    <Layout>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={metaDesc} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
      </Helmet>

      {/* Hero */}
      <section className="bg-slate-50 border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-14">
          <Link to="/categories" className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 hover:text-brand transition-colors">
            ← All Categories
          </Link>
          <h1
            data-testid="category-title"
            className="mt-3 font-display font-black tracking-tight text-3xl sm:text-4xl lg:text-5xl text-slate-900"
          >
            {data.name}
          </h1>
          <p data-testid="category-count" className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#DCFCE7] border border-[#BBF7D0] px-3.5 py-1.5 text-sm font-bold text-[#166534]">
            <ShieldCheck size={15} className="text-[#22C55E]" />
            {data.count} Verified {data.name.split(",")[0].split("&")[0].trim()} {data.count === 1 ? "Business" : "Businesses"}
          </p>
          <p className="mt-5 text-slate-600 max-w-2xl leading-relaxed">{data.description}</p>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-10">
        {data.businesses.length === 0 ? (
          <div data-testid="category-empty" className="min-h-[30vh] flex flex-col items-center justify-center text-center">
            <div className="h-16 w-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
              <Building2 className="text-slate-400" size={28} />
            </div>
            <p className="font-display font-bold text-lg text-slate-700">No verified businesses here yet</p>
            <p className="text-slate-500 mt-1">Be the first verified {data.name} business on IndiaBusinessCircle.</p>
          </div>
        ) : (
          <div data-testid="category-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {data.businesses.map((b) => (
              <BusinessCard key={b.id} business={b} />
            ))}
          </div>
        )}

        {/* CTA */}
        <div
          data-testid="category-cta"
          className="mt-14 rounded-2xl bg-brand text-white px-8 py-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6"
        >
          <div>
            <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight">
              Grow your {data.name.split(",")[0].split("&")[0].trim()} network
            </h2>
            <p className="mt-2 text-white/80 max-w-xl">
              Create your GST-verified profile, get discovered by buyers and partners, and connect with businesses across India.
            </p>
          </div>
          <button
            data-testid="category-cta-btn"
            onClick={() => navigate("/register")}
            className="shrink-0 inline-flex items-center gap-2 rounded-xl bg-white text-brand font-bold px-6 py-3.5 hover:bg-slate-100 transition-colors"
          >
            Join IBC to connect <ArrowRight size={18} />
          </button>
        </div>

        {/* Related categories */}
        {data.related?.length > 0 && (
          <div className="mt-14">
            <div className="flex items-center gap-2 mb-5">
              <Layers size={18} className="text-brand" />
              <h2 className="font-display font-bold text-xl text-slate-900">Related Categories</h2>
            </div>
            <div data-testid="related-categories" className="flex flex-wrap gap-3">
              {data.related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/categories/${r.slug}`}
                  data-testid={`related-cat-${r.slug}`}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:border-brand hover:text-brand transition-colors"
                >
                  {r.name}
                  <span className="text-xs text-slate-400">{r.count}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
